/**
 * Enablement Plan Generator
 *
 * Produces the client-facing enablement plan — a prioritized, financially-quantified
 * roadmap showing cost-of-implementation vs cost-of-inaction for each initiative,
 * organized into a four-quadrant model with configurable time horizons and
 * service tier alignment.
 *
 * Three-layer cost-of-inaction calculation:
 * 1. Control effectiveness (Ponemon-validated, high confidence)
 * 2. Scenario exposure (loss scenario ALE attribution, medium confidence)
 * 3. Sector baseline (claims benchmark fraction, lower confidence)
 */

import claimsBenchmarks from '../data/claimsBenchmarks.json'
import { getAllInitiatives, getPhaseDefinitions } from './roadmapGenerator'

/**
 * Quadrant classification thresholds.
 * Initiatives are classified based on cost-of-inaction (Y axis) vs cost-of-implementation (X axis).
 *
 *   High COI  |  Quick Win  |  Strategic Investment
 *   ----------|-------------|----------------------
 *   Low COI   |  Efficiency |  Defer / Accept
 *             |  Low Cost   |  High Cost
 */
const QUADRANT_THRESHOLDS = {
  coiHighThreshold: 100000,
  implementationHighThreshold: 50000
}

/**
 * Time horizon configurations.
 * Controls the phase timelines and max initiatives per phase.
 */
const TIME_HORIZONS = {
  compressed: {
    id: 'compressed',
    label: 'Compressed (6 months)',
    description: 'Accelerated timeline for urgent risk reduction',
    phases: {
      'quick-wins': { timeline: '0-45 days', maxInitiatives: 10 },
      'strategic': { timeline: '45-120 days', maxInitiatives: 8 },
      'long-term': { timeline: '120-180 days', maxInitiatives: 6 }
    },
    totalMonths: 6
  },
  standard: {
    id: 'standard',
    label: 'Standard (12 months)',
    description: 'Balanced timeline for sustainable program improvement',
    phases: {
      'quick-wins': { timeline: '0-90 days', maxInitiatives: 8 },
      'strategic': { timeline: '90-180 days', maxInitiatives: 6 },
      'long-term': { timeline: '180-365 days', maxInitiatives: 5 }
    },
    totalMonths: 12
  },
  extended: {
    id: 'extended',
    label: 'Extended (18 months)',
    description: 'Longer timeline for resource-constrained organizations',
    phases: {
      'quick-wins': { timeline: '0-120 days', maxInitiatives: 6 },
      'strategic': { timeline: '120-270 days', maxInitiatives: 6 },
      'long-term': { timeline: '270-540 days', maxInitiatives: 6 }
    },
    totalMonths: 18
  }
}

/**
 * Service tier alignment.
 * Defines which initiative categories are most relevant to each service tier
 * and what level of advisory involvement each tier provides.
 */
const SERVICE_TIER_ALIGNMENT = {
  'advisor-access': {
    label: 'Advisor Access',
    focus: 'Governance, policy, and strategic direction',
    primaryCategories: ['governance-and-strategy', 'program-and-risk-reporting', 'risk-and-incident-history'],
    secondaryCategories: ['people-and-culture', 'data-privacy'],
    advisorInvolvement: 'Quarterly reviews and recommendations',
    implementationSupport: 'Guidance only, client executes',
    maxRecommendedInitiatives: 8
  },
  'strategic-partner': {
    label: 'Strategic Partner',
    focus: 'Active governance, operational guidance, and initiative oversight',
    primaryCategories: ['governance-and-strategy', 'incident-readiness', 'access-and-identity', 'data-privacy', 'risk-and-incident-history'],
    secondaryCategories: ['operations-and-technology', 'people-and-culture', 'technology-environment', 'program-and-risk-reporting'],
    advisorInvolvement: 'Monthly sessions with initiative tracking',
    implementationSupport: 'Guidance and oversight, shared execution',
    maxRecommendedInitiatives: 15
  },
  'integrated-ciso': {
    label: 'Integrated CISO',
    focus: 'Full program leadership and operational oversight',
    primaryCategories: ['governance-and-strategy', 'incident-readiness', 'access-and-identity', 'data-privacy', 'operations-and-technology', 'technology-environment', 'risk-and-incident-history', 'security-and-it-program-structure'],
    secondaryCategories: ['people-and-culture', 'data-management', 'program-and-risk-reporting', 'technology-and-systems', 'ai-adoption', 'ai-governance'],
    advisorInvolvement: 'Weekly engagement with direct program leadership',
    implementationSupport: 'Advisor-led execution with client resources',
    maxRecommendedInitiatives: 25
  }
}

/**
 * Format a number as a currency string without the $ sign.
 * @param {number} amount
 * @returns {string} Formatted string (e.g., "292,000" or "1.2M")
 */
function formatCurrency(amount) {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`
  }
  return amount.toLocaleString('en-US')
}

/**
 * Map an initiative to the loss scenario it most directly addresses.
 * Uses causeOfLoss.mapsToScenario to connect initiative categories to scenarios.
 *
 * @param {Object} initiative - Initiative with id and categoryId
 * @returns {Object|null} { scenarioId, attributionFactor } or null
 */
function getInitiativeToScenarioMapping(initiative) {
  const categoryToScenario = {
    'incident-readiness': { scenarioId: 'cyber_extortion', attributionFactor: 0.30 },
    'access-and-identity': { scenarioId: 'data_breach', attributionFactor: 0.25 },
    'people-and-culture': { scenarioId: 'financial_theft', attributionFactor: 0.20 },
    'operations-and-technology': { scenarioId: 'data_breach', attributionFactor: 0.20 },
    'technology-environment': { scenarioId: 'cyber_extortion', attributionFactor: 0.25 },
    'technology-and-systems': { scenarioId: 'third_party', attributionFactor: 0.20 },
    'data-privacy': { scenarioId: 'regulatory', attributionFactor: 0.25 },
    'data-management': { scenarioId: 'data_breach', attributionFactor: 0.15 },
    'governance-and-strategy': { scenarioId: 'regulatory', attributionFactor: 0.15 },
    'risk-and-incident-history': { scenarioId: 'business_interruption', attributionFactor: 0.15 },
    'security-and-it-program-structure': { scenarioId: 'business_interruption', attributionFactor: 0.10 },
    'program-and-risk-reporting': { scenarioId: 'regulatory', attributionFactor: 0.10 }
  }

  return categoryToScenario[initiative.categoryId] || null
}

/**
 * Calculate the annualized cost of not implementing a specific initiative.
 *
 * Three-layer calculation:
 * 1. Control effectiveness layer: If this initiative maps to a Ponemon-validated control,
 *    the cost of inaction is the average cost reduction that control provides.
 * 2. Scenario exposure layer: If this initiative addresses a loss scenario's cause of loss,
 *    the cost of inaction is a fraction of that scenario's ALE.
 * 3. Sector baseline layer: If no specific mapping, use a fraction of the sector's
 *    average incident cost scaled by the initiative's priority.
 *
 * @param {Object} initiative - Initiative from getAllInitiatives() with categoryId
 * @param {Object} sectorProfile - Claims sector profile (from claimsBenchmarks.sectorProfiles)
 * @param {Object} controlEffectivenessMap - Map of initiativeId → averageCostReduction
 * @param {Object[]} lossScenarios - Array of loss scenario results (from calculateLossScenarios)
 * @param {string} priority - Initiative priority from TAPS (High/Medium/Low)
 * @returns {Object} { annualizedCost, methodology, confidence, source, explanation }
 */
export function calculateCostOfInaction(initiative, sectorProfile, controlEffectivenessMap, lossScenarios, priority = 'Medium') {
  // Layer 1: Control effectiveness (highest confidence)
  const controlReduction = controlEffectivenessMap[initiative.id]
  if (controlReduction && controlReduction > 0) {
    return {
      annualizedCost: controlReduction,
      methodology: 'control-effectiveness',
      confidence: 'high',
      source: 'ponemon-2024',
      explanation: `Organizations with ${initiative.name} save an average of $${formatCurrency(controlReduction)} per breach (Ponemon)`
    }
  }

  // Layer 2: Scenario exposure (medium confidence)
  const scenarioMapping = getInitiativeToScenarioMapping(initiative)
  if (scenarioMapping && lossScenarios?.length > 0) {
    const matchedScenario = lossScenarios.find(s => s.scenarioId === scenarioMapping.scenarioId)
    if (matchedScenario?.ale && matchedScenario.ale > 0) {
      const attributionFactor = scenarioMapping.attributionFactor || 0.25
      const annualizedCost = Math.round(matchedScenario.ale * attributionFactor)
      return {
        annualizedCost,
        methodology: 'scenario-exposure',
        confidence: 'medium',
        source: 'loss-scenario-analysis',
        explanation: `Addresses ${Math.round(attributionFactor * 100)}% of ${matchedScenario.scenarioName} exposure ($${formatCurrency(matchedScenario.ale)} ALE)`
      }
    }
  }

  // Layer 3: Sector baseline (lower confidence)
  const sectorAvgCost = sectorProfile?.incidentCost?.fiveYearAverage || 264000
  const priorityMultiplier = { 'High': 0.15, 'Medium': 0.08, 'Low': 0.03 }
  const annualizedCost = Math.round(sectorAvgCost * (priorityMultiplier[priority] || 0.08))

  return {
    annualizedCost,
    methodology: 'sector-baseline',
    confidence: 'low',
    source: sectorProfile?.source || 'sme-aggregate',
    explanation: `Estimated at ${Math.round((priorityMultiplier[priority] || 0.08) * 100)}% of sector average incident cost ($${formatCurrency(sectorAvgCost)})`
  }
}

/**
 * Classify an initiative into one of four quadrants based on
 * cost-of-inaction vs cost-of-implementation.
 *
 * @param {number} costOfInaction - Annualized cost of not implementing
 * @param {number} costOfImplementation - Mid-range implementation cost
 * @param {Object} thresholds - Optional override of QUADRANT_THRESHOLDS
 * @returns {Object} { quadrant, label, color, description }
 */
export function classifyQuadrant(costOfInaction, costOfImplementation, thresholds = QUADRANT_THRESHOLDS) {
  const highCOI = costOfInaction >= thresholds.coiHighThreshold
  const highCost = costOfImplementation >= thresholds.implementationHighThreshold

  if (highCOI && !highCost) {
    return {
      quadrant: 'quick-win',
      label: 'Quick Win',
      color: 'green',
      description: 'High risk reduction at low cost. Prioritize first.'
    }
  }
  if (highCOI && highCost) {
    return {
      quadrant: 'strategic-investment',
      label: 'Strategic Investment',
      color: 'blue',
      description: 'High risk reduction, significant investment. Plan and budget accordingly.'
    }
  }
  if (!highCOI && !highCost) {
    return {
      quadrant: 'efficiency-play',
      label: 'Efficiency Play',
      color: 'amber',
      description: 'Lower risk exposure, low cost. Implement as resources allow.'
    }
  }
  return {
    quadrant: 'defer-accept',
    label: 'Defer / Accept',
    color: 'gray',
    description: 'Lower risk exposure, high cost. Defer unless a specific driver emerges.'
  }
}

/**
 * Generate the complete enablement plan from assessment data.
 *
 * @param {Object} params
 * @param {Object} params.roadmap - Output from generateRoadmap() (has phases with initiatives)
 * @param {Object} params.gapAnalysis - Output from getEnhancedGapAnalysis() (has claimsExposure)
 * @param {Object[]} params.lossScenarios - Output from calculateLossScenarios()
 * @param {Object} params.tapsResults - Output from calculateTAPS()
 * @param {Object} params.scores - Overall maturity scores
 * @param {Object} params.threatContext - Saved threat context (has profileKey, subIndustryKey)
 * @param {Object} params.businessScopingResponses - Business scoping responses
 * @param {Object} params.config - Enablement plan config (from client_enablement_config table)
 * @returns {Object} Complete enablement plan
 */
export function generateEnablementPlan({
  roadmap,
  gapAnalysis,
  lossScenarios,
  tapsResults,
  scores,
  threatContext,
  businessScopingResponses,
  insuranceAdequacy = null,
  config = {}
}) {
  // Resolve configuration with defaults
  const effectiveConfig = {
    timeHorizon: config.time_horizon || 'standard',
    serviceTier: config.service_tier || 'strategic-partner',
    includeCategories: config.include_categories || [],
    excludeInitiatives: config.exclude_initiatives || [],
    priorityOverrides: config.priority_overrides || {},
    showCostOfInaction: config.show_cost_of_inaction !== false,
    maxInitiativesPerPhase: config.max_initiatives_per_phase || null
  }

  // Resolve sector profile from claims benchmarks
  const industry = gapAnalysis?.industry || null
  const sectorProfile = industry ? claimsBenchmarks.sectorProfiles[industry] : null

  // Build control effectiveness map: initiativeId → averageCostReduction
  const controlEffectivenessMap = {}
  const controls = claimsBenchmarks.controlEffectiveness?.controls || []
  controls.forEach(control => {
    if (!control.mapsToInitiative) return
    control.mapsToInitiative.forEach(initId => {
      controlEffectivenessMap[initId] = Math.max(
        controlEffectivenessMap[initId] || 0,
        control.averageCostReduction
      )
    })
  })

  // Get time horizon config
  const timeHorizon = TIME_HORIZONS[effectiveConfig.timeHorizon] || TIME_HORIZONS.standard

  // Get service tier config
  const serviceTier = SERVICE_TIER_ALIGNMENT[effectiveConfig.serviceTier] || SERVICE_TIER_ALIGNMENT['strategic-partner']

  // Get all roadmap initiatives (already prioritized by TAPS)
  const roadmapInitiatives = []
  if (roadmap?.phases) {
    roadmap.phases.forEach(phase => {
      phase.initiatives?.forEach(init => {
        roadmapInitiatives.push({
          ...init,
          sourcePhase: phase.id
        })
      })
    })
  }

  // Synthesize cyber-insurance initiatives from the latest Policy Review.
  // These ride alongside security-control initiatives so the Enablement Plan
  // (and Investment Priorities downstream) reflect insurance gaps as
  // ranked, costed, financially-quantified items.
  const insuranceInitiatives = buildInsuranceInitiatives(insuranceAdequacy)
  insuranceInitiatives.forEach(i => roadmapInitiatives.push(i))

  // Build TAPS priority lookup
  const tapsPriorityMap = {}
  tapsResults?.categories?.forEach(cat => {
    tapsPriorityMap[cat.categoryId] = cat.priorityLevel || 'Medium'
  })

  // Calculate cost-of-inaction and classify each initiative
  const enrichedInitiatives = roadmapInitiatives
    .filter(init => {
      if (effectiveConfig.excludeInitiatives.includes(init.id)) return false
      if (effectiveConfig.includeCategories.length > 0 &&
          !effectiveConfig.includeCategories.includes(init.categoryId)) return false
      return true
    })
    .map(init => {
      // Determine priority (TAPS-based, with admin overrides)
      const priority = effectiveConfig.priorityOverrides[init.id]
        || tapsPriorityMap[init.categoryId]
        || 'Medium'

      // Calculate cost of inaction
      const costOfInaction = calculateCostOfInaction(
        init,
        sectorProfile,
        controlEffectivenessMap,
        lossScenarios,
        priority
      )

      // Calculate mid-range implementation cost
      const implCostMid = Math.round(((init.scaledCost?.min || init.costRange?.min || 0) + (init.scaledCost?.max || init.costRange?.max || 0)) / 2)

      // Classify into quadrant
      const quadrant = classifyQuadrant(costOfInaction.annualizedCost, implCostMid)

      // Calculate net value (cost of inaction - implementation cost)
      const netValue = costOfInaction.annualizedCost - implCostMid

      // Service tier relevance
      const isTierPrimary = serviceTier.primaryCategories.includes(init.categoryId)
      const isTierSecondary = serviceTier.secondaryCategories.includes(init.categoryId)
      const tierRelevance = isTierPrimary ? 'primary' : isTierSecondary ? 'secondary' : 'supplemental'

      return {
        ...init,
        priority,
        costOfInaction,
        implementationCost: {
          min: init.scaledCost?.min || init.costRange?.min || 0,
          max: init.scaledCost?.max || init.costRange?.max || 0,
          mid: implCostMid,
          formatted: `$${formatCurrency(init.scaledCost?.min || init.costRange?.min || 0)} - $${formatCurrency(init.scaledCost?.max || init.costRange?.max || 0)}`
        },
        quadrant,
        netValue,
        netValueFormatted: `$${formatCurrency(Math.abs(netValue))}${netValue >= 0 ? ' net positive' : ' net negative'}`,
        tierRelevance,
        status: 'not-started'
      }
    })
    .sort((a, b) => {
      const quadrantOrder = { 'quick-win': 0, 'strategic-investment': 1, 'efficiency-play': 2, 'defer-accept': 3 }
      const qDiff = (quadrantOrder[a.quadrant.quadrant] || 4) - (quadrantOrder[b.quadrant.quadrant] || 4)
      if (qDiff !== 0) return qDiff
      return b.netValue - a.netValue
    })

  // Group by phase with time horizon constraints
  const phaseGroups = {}
  const phaseConfig = timeHorizon.phases
  enrichedInitiatives.forEach(init => {
    const phaseId = init.sourcePhase || init.phase || 'strategic'
    if (!phaseGroups[phaseId]) {
      phaseGroups[phaseId] = {
        id: phaseId,
        timeline: phaseConfig[phaseId]?.timeline || 'TBD',
        maxInitiatives: effectiveConfig.maxInitiativesPerPhase?.[phaseId]
          || phaseConfig[phaseId]?.maxInitiatives
          || 10,
        initiatives: []
      }
    }
    if (phaseGroups[phaseId].initiatives.length < phaseGroups[phaseId].maxInitiatives) {
      phaseGroups[phaseId].initiatives.push(init)
    }
  })

  // Calculate summary metrics
  const allIncluded = Object.values(phaseGroups).flatMap(p => p.initiatives)
  const totalImplementationCost = allIncluded.reduce((sum, i) => sum + i.implementationCost.mid, 0)
  const totalCostOfInaction = allIncluded.reduce((sum, i) => sum + i.costOfInaction.annualizedCost, 0)
  const totalNetValue = totalCostOfInaction - totalImplementationCost

  const quadrantCounts = allIncluded.reduce((acc, i) => {
    acc[i.quadrant.quadrant] = (acc[i.quadrant.quadrant] || 0) + 1
    return acc
  }, {})

  const confidenceCounts = allIncluded.reduce((acc, i) => {
    acc[i.costOfInaction.confidence] = (acc[i.costOfInaction.confidence] || 0) + 1
    return acc
  }, {})

  return {
    phases: Object.values(phaseGroups),
    initiatives: allIncluded,
    summary: {
      totalInitiatives: allIncluded.length,
      totalImplementationCost,
      totalImplementationCostFormatted: `$${formatCurrency(totalImplementationCost)}`,
      totalCostOfInaction,
      totalCostOfInactionFormatted: `$${formatCurrency(totalCostOfInaction)}`,
      totalNetValue,
      totalNetValueFormatted: `$${formatCurrency(Math.abs(totalNetValue))}${totalNetValue >= 0 ? ' net positive' : ' net negative'}`,
      quadrantDistribution: quadrantCounts,
      confidenceDistribution: confidenceCounts,
      timeHorizon: {
        id: timeHorizon.id,
        label: timeHorizon.label,
        totalMonths: timeHorizon.totalMonths
      },
      serviceTier: {
        id: effectiveConfig.serviceTier,
        label: serviceTier.label,
        focus: serviceTier.focus,
        advisorInvolvement: serviceTier.advisorInvolvement
      },
      sectorContext: sectorProfile ? {
        sector: industry,
        avgIncidentCost: sectorProfile.incidentCost?.fiveYearAverage || null,
        topCauseOfLoss: sectorProfile.topCausesOfLoss?.[0] || null,
        source: sectorProfile.source
      } : null
    },
    config: effectiveConfig,
    generatedAt: new Date().toISOString()
  }
}

/**
 * Get available time horizon options for admin configuration.
 * @returns {Object[]} Array of { id, label, description, totalMonths }
 */
export function getTimeHorizonOptions() {
  return Object.values(TIME_HORIZONS).map(th => ({
    id: th.id,
    label: th.label,
    description: th.description,
    totalMonths: th.totalMonths
  }))
}

/**
 * Get available service tier options for admin configuration.
 * @returns {Object[]} Array of { id, label, focus, advisorInvolvement, maxRecommendedInitiatives }
 */
export function getServiceTierOptions() {
  return Object.entries(SERVICE_TIER_ALIGNMENT).map(([id, tier]) => ({
    id,
    label: tier.label,
    focus: tier.focus,
    advisorInvolvement: tier.advisorInvolvement,
    maxRecommendedInitiatives: tier.maxRecommendedInitiatives
  }))
}

/**
 * Generate an executive-level narrative summary of the enablement plan.
 * This is consumed by report exports and the executive summary display.
 *
 * @param {Object} plan - Output from generateEnablementPlan()
 * @returns {Object} { headline, keyFindings, recommendation, investmentSummary, immediateActions }
 */
export function generateExecutiveNarrative(plan) {
  const s = plan.summary
  const quickWinPhase = plan.phases.find(p => p.id === 'quick-wins')
  const quickWinCount = quickWinPhase?.initiatives?.length || 0
  const topQuickWins = quickWinPhase?.initiatives?.slice(0, 3).map(i => i.name) || []

  // Find the initiative with highest net value
  const topNetValue = plan.initiatives.reduce((best, init) =>
    init.netValue > (best?.netValue || 0) ? init : best, null)

  // Find highest-confidence cost-of-inaction initiative
  const topConfidence = plan.initiatives.find(i => i.costOfInaction.confidence === 'high')

  return {
    headline: `${s.totalInitiatives} initiatives across ${plan.phases.length} phases to reduce risk exposure of $${formatCurrency(s.totalCostOfInaction)} annually`,
    investmentSummary: {
      totalInvestment: s.totalImplementationCostFormatted,
      totalExposureReduction: s.totalCostOfInactionFormatted,
      netValue: s.totalNetValueFormatted,
      timeHorizon: s.timeHorizon.label,
      serviceTier: s.serviceTier.label
    },
    keyFindings: [
      s.sectorContext?.avgIncidentCost
        ? `Organizations in ${s.sectorContext.sector} face an average incident cost of $${formatCurrency(s.sectorContext.avgIncidentCost)}, primarily driven by ${(s.sectorContext.topCauseOfLoss || 'multiple threat vectors').toLowerCase()}.`
        : null,
      topConfidence
        ? `${topConfidence.name} alone represents $${formatCurrency(topConfidence.costOfInaction.annualizedCost)} in annual risk exposure based on validated breach cost data.`
        : null,
      quickWinCount > 0
        ? `${quickWinCount} quick-win initiatives can be implemented within ${quickWinPhase?.timeline || '90 days'} for immediate risk reduction.`
        : null,
      topNetValue
        ? `Highest-value initiative: ${topNetValue.name}, $${formatCurrency(topNetValue.netValue)} net positive return.`
        : null
    ].filter(Boolean),
    recommendation: `Based on the ${s.serviceTier.label} engagement model with ${s.serviceTier.advisorInvolvement.toLowerCase()}, we recommend prioritizing the ${quickWinCount} quick-win initiatives immediately, followed by strategic investments in ${s.quadrantDistribution['strategic-investment'] || 0} higher-cost, higher-impact areas.`,
    immediateActions: topQuickWins
  }
}

// Synthesize cyber-insurance enablement initiatives from a computed
// insuranceAdequacy result. Returns an array of roadmap-shape initiatives
// (id, name, categoryId, phase, costRange, effort) so the rest of the
// pipeline (cost-of-inaction, quadrant, netValue, tierRelevance) treats
// them identically to security-control initiatives.
function buildInsuranceInitiatives(insuranceAdequacy) {
  if (!insuranceAdequacy) return []
  const out = []
  const adequacy = insuranceAdequacy.overallAdequacy
  const isInadequate = adequacy === 'Inadequate' || adequacy === 'Partial' || adequacy === 'None'

  // Coverage uplift initiative
  const coverageLimit = Number(insuranceAdequacy.coverageLimit) || 0
  const recommendedCoverage = Number(insuranceAdequacy.recommendedCoverage) || 0
  if (isInadequate && recommendedCoverage > 0 && coverageLimit < recommendedCoverage) {
    const gap = recommendedCoverage - coverageLimit
    // Annual premium increase estimate: roughly 0.5% – 1.2% of additional limit
    out.push({
      id: 'insurance-coverage-uplift',
      name: `Increase cyber insurance limit to $${formatCurrency(recommendedCoverage)}`,
      categoryId: 'cyber-insurance',
      phase: 'quick-wins',
      effort: 'Low',
      costRange: {
        min: Math.max(5000, Math.round(gap * 0.005)),
        max: Math.max(15000, Math.round(gap * 0.012))
      },
      rationale: insuranceAdequacy.recommendedCoverageRationale || 'Aligns coverage with industry benchmark and modeled exposure',
      sourcePhase: 'quick-wins'
    })
  }

  // Sublimit / exclusion remediation initiative
  const reviewFindings = insuranceAdequacy.findings || null
  const reviewGaps = Array.isArray(reviewFindings?.gaps) ? reviewFindings.gaps : []
  const highSeverityGaps = reviewGaps.filter(g => /high|critical/i.test(g.severity || ''))
  if (highSeverityGaps.length > 0) {
    out.push({
      id: 'insurance-policy-gap-remediation',
      name: `Close ${highSeverityGaps.length} high-severity policy gap${highSeverityGaps.length > 1 ? 's' : ''} from latest review`,
      categoryId: 'cyber-insurance',
      phase: 'quick-wins',
      effort: 'Medium',
      costRange: { min: 8000, max: 25000 },
      rationale: `Latest insurance review flagged ${highSeverityGaps.length} high-severity gap${highSeverityGaps.length > 1 ? 's' : ''}: ${highSeverityGaps.slice(0, 2).map(g => g.title || 'gap').join(', ')}${highSeverityGaps.length > 2 ? '…' : ''}`,
      sourcePhase: 'quick-wins'
    })
  }

  // Deductible / retention rebalance
  const deductible = Number(insuranceAdequacy.deductible) || 0
  if (deductible > 50000) {
    out.push({
      id: 'insurance-retention-rebalance',
      name: 'Rebalance retention to reduce out-of-pocket exposure',
      categoryId: 'cyber-insurance',
      phase: 'strategic',
      effort: 'Low',
      costRange: { min: 2000, max: 8000 },
      rationale: `Current $${deductible.toLocaleString()} retention requires significant pre-coverage cash; broker review may identify lower-retention options at acceptable premium impact.`,
      sourcePhase: 'strategic'
    })
  }

  return out
}

export {
  QUADRANT_THRESHOLDS,
  TIME_HORIZONS,
  SERVICE_TIER_ALIGNMENT
}

export default {
  calculateCostOfInaction,
  classifyQuadrant,
  generateEnablementPlan,
  getTimeHorizonOptions,
  getServiceTierOptions,
  generateExecutiveNarrative
}
